import { City, Country } from 'country-state-city';
import React, { useEffect, useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import profileplaceholder from '../../assets/placeholderprofile.png';
import { updateProfile } from '../../features/profile/profileSlice';
import OtpModal from '../Auth/OtpModal';
import ChangePhotoModal from './ChangePhotoModal';
// @ts-ignore
import 'react-phone-input-2/lib/style.css';
import { BASE_URL } from '../../utils/constants';
import { getAuthContext } from '../../utils/jwt';

// const FiArrowLeftComponent = FiArrowLeft as unknown as React.ComponentType<{
//   size?: number | string;
//   className?: string;
// }>;

interface ProfileData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  companyName: string;
  companyType: string;
  website: string;
  country: string;
  city: string;
  address: string;
  postalCode: string;
  profilePhoto: string;
}

interface EditProfileProps {
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
  onUpdate: (data: ProfileData) => void;
  initialData: ProfileData;
}

const EditProfile: React.FC<EditProfileProps> = ({ setIsEditing, onUpdate, initialData }) => {
  const [formData, setFormData] = useState<ProfileData>(initialData);
  const [initialFormData, setInitialFormData] = useState<ProfileData>(initialData);
  const [showModal, setShowModal] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEmailChangeOnly, setIsEmailChangeOnly] = useState(false);

  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(60);
  const [resendDisabled, setResendDisabled] = useState(true);
  const [newEmailForOtp, setNewEmailForOtp] = useState('');

  const dispatch = useDispatch();

  useEffect(() => {
    setFormData(initialData);
    setInitialFormData(initialData);
  }, [initialData]);

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const countryIso = e.target.value;
    setFormData((prev) => {
      const newData = { ...prev, country: countryIso, city: '' };
      if (countryIso) {
        const country = Country.getCountryByCode(countryIso);
        if (country?.phonecode) {
          newData.phone = `+${country.phonecode}`;
        }
      }
      return newData;
    });
  };

  const handlePhoneChange = (value: string, countryData: any) => {
    setFormData((prev) => {
      const newData = { ...prev, phone: value };
      if (countryData?.countryCode) {
        const isoCode = countryData.countryCode.toUpperCase();
        if (isoCode !== prev.country) {
          newData.country = isoCode;
          newData.city = '';
        }
      }
      return newData;
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1 || !/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-input-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handleResendOtp = async () => {
    if (!newEmailForOtp) return;
    const { token, tenantId, userId } = getAuthContext();
    if (!token) return;


    try {
      const res = await fetch(`${BASE_URL}/v1/profile/me/email/change/resend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ newEmail: newEmailForOtp }),
      });

      if (res.ok) {
        toast.success('OTP resent successfully');
        setTimer(60);
        setResendDisabled(true);
        setOtp(['', '', '', '', '', '']);
        const interval = setInterval(() => {
          setTimer((prev) => {
            if (prev <= 1) {
              clearInterval(interval);
              setResendDisabled(false);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        toast.error('Failed to resend OTP');
      }
    } catch {
      toast.error('Failed to resend OTP');
    }
  };

  const handleVerifyOtp = async () => {
    const enteredOtp = otp.join('');
    if (enteredOtp.length !== 6) return;

    const { token, tenantId, userId } = getAuthContext();
    if (!token) return;


    try {
      const response = await fetch(`${BASE_URL}/v1/profile/me/email/change/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          newEmail: newEmailForOtp,
          otp: enteredOtp,
        }),
      });

      if (response.ok) {
        toast.success('Email updated successfully!');
        setShowOtpModal(false);

        setFormData((prev) => ({ ...prev, email: newEmailForOtp }));
        setInitialFormData((prev) => ({ ...prev, email: newEmailForOtp }));

        if (isEmailChangeOnly) {
          const updatedData = { ...formData, email: newEmailForOtp };
          localStorage.setItem('profileData', JSON.stringify(updatedData));
          onUpdate(updatedData);
          setIsEditing(false);
        } else {
          await updateOtherFields(newEmailForOtp);
        }
      } else {
        toast.error('Invalid OTP. Please try again.');
      }
    } catch (error) {
      toast.error('Failed to verify OTP');
    }
  };

  const updateOtherFields = async (newEmail?: string) => {
    const { token, tenantId, userId } = getAuthContext();
    if (!token) {
      toast.error('Authentication token not found');
      return;
    }


    try {
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        companyName: formData.companyName,
        companyType: formData.companyType,
        website: formData.website,
        country: formData.country,
        city: formData.city,
        address: formData.address,
        postalCode: formData.postalCode,
      };

      if (newEmail) Object.assign(payload, { email: newEmail });

      const response = await fetch(`${BASE_URL}/v1/profile/me`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to update profile');
      }

      const updatedData = newEmail ? { ...formData, email: newEmail } : formData;
      dispatch(updateProfile(updatedData));
      localStorage.setItem('profileData', JSON.stringify(updatedData));
      onUpdate(updatedData);

      toast.success('Profile updated successfully');
      setIsEditing(false);
    } catch (error: any) {
      // console.error('Profile update failed:', error);
      toast.error(error.message || 'Failed to update profile');
    }
  };

  const requestEmailChange = async (newEmail: string): Promise<boolean> => {
    const { token, tenantId, userId } = getAuthContext();
    if (!token) {
      toast.error('Authentication token not found');
      return false;
    }


    try {
      const response = await fetch(`${BASE_URL}/v1/profile/me/email/change/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ newEmail }),
      });

      let responseData;
      try { responseData = await response.json(); } catch { responseData = null; }

      if (!response.ok) {
        const errorMessage = responseData?.message || responseData?.error || 'Failed to send OTP';
        toast.error(errorMessage);
        return false;
      }

      toast.success(`OTP sent successfully to ${newEmail}`);
      setNewEmailForOtp(newEmail);
      setOtp(['', '', '', '', '', '']);
      setTimer(60);
      setResendDisabled(true);
      setShowOtpModal(true);

      const interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setResendDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return true;
    } catch (error: any) {
      toast.error(error.message || 'Failed to send OTP');
      return false;
    }
  };

  const handleUpdate = async () => {
    setLoading(true);

    const emailChanged = formData.email !== initialFormData.email;
    const otherFieldsChanged =
      formData.firstName !== initialFormData.firstName ||
      formData.lastName !== initialFormData.lastName ||
      formData.phone !== initialFormData.phone ||
      formData.companyName !== initialFormData.companyName ||
      formData.companyType !== initialFormData.companyType ||
      formData.website !== initialFormData.website ||
      formData.country !== initialFormData.country ||
      formData.city !== initialFormData.city ||
      formData.address !== initialFormData.address ||
      formData.postalCode !== initialFormData.postalCode;

    try {
      if (emailChanged && !otherFieldsChanged) {
        setIsEmailChangeOnly(true);
        const success = await requestEmailChange(formData.email);
        if (!success) setLoading(false);
        return;
      }

      if (emailChanged && otherFieldsChanged) {
        setIsEmailChangeOnly(false);
        const emailSuccess = await requestEmailChange(formData.email);
        if (!emailSuccess) {
          setLoading(false);
          return;
        }
        return;
      }

      if (otherFieldsChanged && !emailChanged) {
        await updateOtherFields();
        setLoading(false);
        return;
      }

      if (!emailChanged && !otherFieldsChanged) {
        toast.info('No changes to update');
        setIsEditing(false);
        setLoading(false);
        return;
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to update profile');
      setLoading(false);
    }
  };

  const handleBack = () => setIsEditing(false);

  const handleSavePhoto = (photoUrl: string) => {
    const updatedData = { ...formData, profilePhoto: photoUrl };
    setFormData(updatedData);
    localStorage.setItem('profileData', JSON.stringify(updatedData));
    dispatch(updateProfile(updatedData));
    onUpdate(updatedData);
    setShowModal(false);
  };

  const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialFormData);
  const cities = formData.country ? City.getCitiesOfCountry(formData.country) || [] : [];

  return (
    <div>
      <div className='flex justify-between'>
        <div className='flex items-center mb-6'>
          <img
            src={formData.profilePhoto || profileplaceholder}
            alt='Profile'
            className='rounded-full object-cover mr-4'
            style={{ width: '108px', height: '108px' }}
          />
          <div>
            <h3 className='text-lg font-semibold' style={{ color: '#3F5265', fontSize: '20px' }}>
              Upload your photo
            </h3>
            <p style={{ color: '#657584', fontSize: '16px' }} className='mt-2 text-gray text-sm'>
              Photo should be at least 512x512px
            </p>
            <button
              onClick={() => setShowModal(true)}
              className='mt-4 flex items-center gap-1 text-sky-600 border border-sky-200 rounded px-3 py-2 text-xs hover:bg-sky-50'
              style={{ color: '#00A4BD', borderColor: '#00A4BD', fontWeight: 500 }}
            >
              Change Photo
            </button>
          </div>
        </div>

        <div className='flex gap-2 items-start'>
          <button onClick={handleBack} className='px-3 py-1.5 border border-orange-500 text-orange-500 rounded hover:bg-orange-50'>
            Cancel
          </button>
          <button
            onClick={handleUpdate}
            disabled={!hasChanges || loading}
            className={`px-3 py-1.5 rounded ${hasChanges && !loading ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-gray-300 text-white cursor-not-allowed'}`}
          >
            {loading ? 'Updating...' : 'Update'}
          </button>
        </div>
      </div>

      <div className='grid grid-cols-2 gap-6 text-sm py-2'>
        <div>
          <span className='text-xs text-gray-400 block mb-1'>EMAIL <span className='text-red-500'>*</span></span>
          <input type='email' name='email' value={formData.email} onChange={handleChange} className='w-full rounded px-3 py-3 focus:outline-none focus:border-blue-500' style={{ backgroundColor: '#F5F8FA', color: '#3F5265' }} />
        </div>

        <div className='flex space-x-6'>
          <div className='w-1/2'>
            <span className='text-xs text-gray-400 block mb-1'>FIRST NAME <span className='text-red-500'>*</span></span>
            <input type='text' name='firstName' value={formData.firstName} onChange={handleChange} className='w-full bg-blue-50 rounded px-3 py-3 focus:outline-none focus:border-blue-500' style={{ backgroundColor: '#F5F8FA', color: '#3F5265' }} />
          </div>
          <div className='w-1/2'>
            <span className='text-xs text-gray-400 block mb-1'>LAST NAME <span className='text-red-500'>*</span></span>
            <input type='text' name='lastName' value={formData.lastName} onChange={handleChange} className='w-full bg-blue-50 rounded px-3 py-3 focus:outline-none focus:border-blue-500' style={{ backgroundColor: '#F5F8FA', color: '#3F5265' }} />
          </div>
        </div>

        <div>
          <span className='text-xs text-gray-400 block mb-1'>ROLE</span>
          <input type='text' value='Super Admin' className='w-full rounded px-3 py-3 focus:outline-none' readOnly style={{ backgroundColor: '#E1EAF0', color: '#9FA9B2' }} />
        </div>

        <div>
          <span className='text-xs text-gray-400 block mb-1'>PHONE NUMBER</span>
          <PhoneInput country={'us'} value={formData.phone} onChange={handlePhoneChange} inputClass='w-full bg-blue-50 rounded px-3 py-3 focus:outline-none focus:border-blue-500' containerClass='w-full' buttonClass='bg-blue-50' inputStyle={{ backgroundColor: '#F5F8FA', color: '#3F5265', width: '100%', height: '50px', fontSize: '16px' }} />
        </div>

        <div>
          <span className='text-xs text-gray-400 block mb-1'>COMPANY NAME <span className='text-red-500'>*</span></span>
          <input type='text' name='companyName' value={formData.companyName} onChange={handleChange} className='w-full bg-blue-50 rounded px-3 py-3 focus:outline-none focus:border-blue-500' style={{ backgroundColor: '#F5F8FA', color: '#3F5265' }} />
        </div>

        <div>
          <span className='text-xs text-gray-400 block mb-1'>COMPANY TYPE <span className='text-red-500'>*</span></span>
          <select name='companyType' value={formData.companyType} onChange={handleChange} className='w-full bg-blue-50 rounded px-3 py-3 focus:outline-none focus:border-blue-500' style={{ backgroundColor: '#F5F8FA', color: '#3F5265' }}>
            <option value="">Select Type</option>
            <option value='News'>News</option>
            <option value='Tech'>Tech</option>
            <option value='Finance'>Finance</option>
          </select>
        </div>

        <div>
          <span className='text-xs text-gray-400 block mb-1'>WEBSITE</span>
          <input type='url' name='website' value={formData.website} onChange={handleChange} className='w-full bg-blue-50 rounded px-3 py-3 focus:outline-none focus:border-blue-500' style={{ backgroundColor: '#F5F8FA', color: '#3F5265' }} />
        </div>
      </div>

      <div className='grid grid-cols-3 gap-6 text-sm py-2 mt-6'>
        <div>
          <span className='text-xs text-gray-400 block mb-1'>COUNTRY <span className='text-red-500'>*</span></span>
          <select name='country' value={formData.country} onChange={handleCountryChange} className='w-full bg-blue-50 rounded px-3 py-3 focus:outline-none focus:border-blue-500' style={{ backgroundColor: '#F5F8FA', color: '#3F5265' }}>
            <option value="">Select Country</option>
            {Country.getAllCountries().map((country) => (
              <option key={country.isoCode} value={country.isoCode}>{country.name}</option>
            ))}
          </select>
        </div>

        <div>
          <span className='text-xs text-gray-400 block mb-1'>CITY <span className='text-red-500'>*</span></span>
          <select name='city' value={formData.city} onChange={handleChange} disabled={!formData.country} className='w-full bg-blue-50 rounded px-3 py-3 focus:outline-none focus:border-blue-500' style={{ backgroundColor: '#F5F8FA', color: '#3F5265' }}>
            <option value="">Select City</option>
            {cities.map((city) => <option key={city.name} value={city.name}>{city.name}</option>)}
          </select>
        </div>

        <div>
          <span className='text-xs text-gray-400 block mb-1'>ADDRESS</span>
          <input type='text' name='address' value={formData.address} onChange={handleChange} className='w-full bg-blue-50 rounded px-3 py-3 focus:outline-none focus:border-blue-500' style={{ backgroundColor: '#F5F8FA', color: '#3F5265' }} />
        </div>

        <div>
          <span className='text-xs text-gray-400 block mb-1'>POSTAL CODE</span>
          <input type='text' name='postalCode' value={formData.postalCode} onChange={handleChange} className='w-full bg-blue-50 rounded px-3 py-3 focus:outline-none focus:border-blue-500' style={{ backgroundColor: '#F5F8FA', color: '#3F5265' }} />
        </div>
      </div>

      {showModal && <ChangePhotoModal onClose={() => setShowModal(false)} onSave={handleSavePhoto} currentPhotoUrl={formData.profilePhoto} />}

      {showOtpModal && (
        <OtpModal
          email={newEmailForOtp}
          otp={otp}
          timer={timer}
          resendDisabled={resendDisabled}
          isOtpValid={otp.every((d) => d !== '')}
          onOtpChange={handleOtpChange}
          onOtpKeyDown={handleOtpKeyDown}
          onResend={handleResendOtp}
          onContinue={handleVerifyOtp}
          onClose={() => {
            setShowOtpModal(false);
            setFormData(initialFormData);
          }}
        />
      )}
    </div>
  );
};

export default EditProfile;